import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { ItemThumb } from '@/components/ui/ItemThumb';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import {
  listWarehouseLedger,
  type WarehouseLedgerRow,
} from '@/api/warehouseStock';
import { formatDate, formatINRSymbol } from '@/lib/format';

const TXN_TONE: Record<string, 'success' | 'danger' | 'accent' | 'muted'> = {
  'GRN Received': 'success',
  'DO Issued': 'danger',
  'Manual Adjust In': 'accent',
  'Manual Adjust Out': 'accent',
};

export function WarehouseLedgerPage() {
  const [params, setParams] = useSearchParams();
  const initialItemId = params.get('itemId') ?? '';
  const [search, setSearch] = useState('');
  const [txnType, setTxnType] = useState('');
  const [itemId, setItemId] = useState(initialItemId);

  const filter = useMemo(
    () => ({
      txnType: txnType || undefined,
      itemId: itemId ? Number(itemId) : undefined,
    }),
    [txnType, itemId],
  );

  const query = useQuery({
    queryKey: ['warehouse-ledger', filter],
    queryFn: () => listWarehouseLedger(filter),
    refetchInterval: 60_000,
  });
  const rows = query.data ?? [];

  function clearItem() {
    setItemId('');
    if (params.get('itemId')) {
      params.delete('itemId');
      setParams(params, { replace: true });
    }
  }

  return (
    <div>
      <SectionHeader title="Warehouse Ledger" />

      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto sm:min-w-[220px]">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="!pl-5 sm:!pl-8"
              aria-label="Search warehouse ledger"
            />
          </div>
        </FilterField>
        <FilterField label="Txn Type">
          <Select value={txnType} onChange={(e) => setTxnType(e.target.value)}>
            <option value="">All</option>
            <option>GRN Received</option>
            <option>Goods Out</option>
            <option>STN Dispatched</option>
            <option>STN Received</option>
            <option>Manual Adjust In</option>
            <option>Manual Adjust Out</option>
          </Select>
        </FilterField>
        {itemId && (
          <button
            type="button"
            onClick={clearItem}
            className="col-span-3 inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:col-span-1 sm:ml-auto sm:h-8 sm:self-end sm:px-3 sm:text-xs"
          >
            Clear item filter ({itemId})
          </button>
        )}
      </FilterBar>

      <Table<WarehouseLedgerRow>
        paginated
        storageKey="warehouse-ledger"
        title="Stock Movements"
        count={`${rows.length} txns`}
        rows={rows}
        search={search}
        rowKey={(r) => r.id}
        loading={query.isFetching && rows.length === 0}
        columns={[
          { key: 'date', header: 'Date', cell: (r) => formatDate(r.txnDate) },
          {
            key: 'type',
            header: 'Type',
            cell: (r) => (
              <Badge tone={TXN_TONE[r.txnType] ?? 'muted'}>{r.txnType}</Badge>
            ),
          },
          {
            key: 'ref',
            header: 'Ref Doc',
            cell: (r) =>
              r.refDoc ? (
                <span className="text-[11px]">{r.refDoc}</span>
              ) : (
                '—'
              ),
          },
          {
            key: 'code',
            header: 'Item Code',
            cell: (r) => <span className="font-extrabold text-accent">{r.itemCode}</span>,
          },
          {
            key: 'name',
            header: 'Item Name',
            cell: (r) => (
              <div className="flex items-center gap-2">
                <ItemThumb src={r.imageUrl} size={28} />
                <span>{r.itemName}</span>
              </div>
            ),
          },
          {
            key: 'barcode',
            header: 'SKU Barcode',
            cell: (r) => r.barcode ?? <span className="text-[var(--text-muted)]">—</span>,
          },
          { key: 'unit', header: 'Unit', cell: (r) => r.unit },
          {
            key: 'in',
            header: 'In',
            align: 'right',
            cell: (r) =>
              r.qtyIn > 0 ? (
                <span className="font-semibold text-success">+{r.qtyIn}</span>
              ) : (
                '—'
              ),
          },
          {
            key: 'out',
            header: 'Out',
            align: 'right',
            cell: (r) =>
              r.qtyOut > 0 ? (
                <span className="font-semibold text-danger">-{r.qtyOut}</span>
              ) : (
                '—'
              ),
          },
          {
            key: 'rate',
            header: 'Rate',
            align: 'right',
            cell: (r) => (r.rate ? formatINRSymbol(r.rate) : '—'),
          },
          {
            key: 'value',
            header: 'Value',
            align: 'right',
            cell: (r) =>
              r.value ? (
                <span className={`font-semibold ${r.qtyIn > 0 ? 'text-success' : 'text-danger'}`}>
                  {formatINRSymbol(r.value)}
                </span>
              ) : (
                '—'
              ),
          },
          {
            key: 'bal',
            header: 'Balance',
            align: 'right',
            cell: (r) => <strong>{r.balance}</strong>,
          },
          {
            key: 'remarks',
            header: 'Remarks',
            cell: (r) =>
              r.remarks ? (
                <span className="text-[11px] text-[var(--text-muted)]">{r.remarks}</span>
              ) : (
                '—'
              ),
          },
        ]}
      />
    </div>
  );
}
